import { z } from 'zod';
import { bankOperationSchema, bankCoverageSchema, missingBankPeriods, reconciliationDate } from './reconciliation.js';
const money = z.number().int().min(0).max(200_000_000_000);
export const taxRowSchema = z.object({ operation: bankOperationSchema, category: z.enum(['income', 'expense', 'exclude', 'refund', 'review']), taxKop: money, reason: z.string().max(2000), reviewed: z.boolean(), decision: z.enum(['auto', 'manual']).optional(), refundOf: z.string().max(100).default('') });
const allocation = z.object({ fixedKop: money, previousExtraKop: money, currentExtraKop: money });
export const myTaxSchema = z.object({
    year: z.literal(2026), ownInn: z.string().regex(/^\d{12}$/), account: z.string().regex(/^\d{20}$/),
    profileConfirmed: z.boolean(), sourcesConfirmed: z.boolean(), previousIncomeKop: money.nullable(), previousExtraUsedKop: money, previousExtraConfirmed: z.boolean(),
    rows: z.array(taxRowSchema).max(20000), coverage: z.array(bankCoverageSchema).max(2000),
    allocations: z.array(allocation).length(4),
    payments: z.array(z.object({ id: z.string().uuid(), date: reconciliationDate, amountKop: money.refine(n => n > 0), source: z.enum(['business', 'personal']), kind: z.enum(['ens', 'usn', 'fixed', 'extra']), reference: z.string().min(1).max(300), confirmed: z.boolean() })).max(1000),
});
export const TAX_FIXED_2026 = 5739000;
export function taxExtra(incomeKop, year) { return Math.min(Math.max(0, Math.round((incomeKop - 30000000) / 100)), year === 2026 ? 32181800 : 30088800); }
export function newMyTax(ownInn, account) { return { year: 2026, ownInn, account, profileConfirmed: false, sourcesConfirmed: false, previousIncomeKop: null, previousExtraUsedKop: 0, previousExtraConfirmed: false, rows: [], coverage: [], allocations: Array.from({ length: 4 }, () => ({ fixedKop: 0, previousExtraKop: 0, currentExtraKop: 0 })), payments: [] }; }
export function suggestTaxRow(operation) {
    const text = (operation.purpose + ' ' + operation.counterpartyName).toLowerCase();
    if (operation.direction === 'refund')
        return { operation, category: /возврат/.test(text) ? 'review' : 'expense', taxKop: 0, reason: /возврат/.test(text) ? 'Проверьте, является ли это возвратом облагаемой оплаты' : 'Расход не уменьшает УСН «Доходы»', reviewed: !/возврат/.test(text), refundOf: '' };
    const suspect = operation.counterpartyInn === operation.ownInn || /собственн|личн|за[её]м|займ|ссуд|взнос|кредит|возврат|процент|эквайр|комисси|реестр|маркетплейс/.test(text);
    const recognizable = /оплат|аванс|расч[её]т за|сч[её]т|изготовлен|ортодонт|протез/.test(text);
    return { operation, category: suspect || !recognizable ? 'review' : 'income', taxKop: operation.amountKop, reason: suspect ? 'Нужна проверка налогового назначения' : recognizable ? 'Оплата из выписки — учтена автоматически' : 'Неясное назначение поступления', reviewed: !suspect && recognizable, decision: 'auto', refundOf: '' };
}
export function mergeTaxImport(model, operations, coverage) {
    const rows = new Map(model.rows.map(r => [r.operation.key, r]));
    let added = 0, duplicates = 0;
    for (const o of operations) {
        if (o.ownInn !== model.ownInn || o.ownAccount !== model.account || !o.date.startsWith('2026-'))
            continue;
        if (rows.has(o.key)) {
            duplicates++;
            continue;
        }
        rows.set(o.key, suggestTaxRow(o));
        added++;
    }
    const spans = new Map(model.coverage.map(c => [`${c.account}|${c.from}|${c.to}`, c]));
    for (const c of coverage)
        if (c.ownInn === model.ownInn && c.account === model.account)
            spans.set(`${c.account}|${c.from}|${c.to}`, c);
    return { model: { ...model, sourcesConfirmed: model.sourcesConfirmed, rows: prepareTaxRows([...rows.values()].sort((a, b) => a.operation.date.localeCompare(b.operation.date) || a.operation.key.localeCompare(b.operation.key))), coverage: [...spans.values()] }, added, duplicates };
}
export function taxValidation(model) {
    const errors = [], keys = new Set(), refunds = new Map();
    for (const r of model.rows) {
        const o = r.operation;
        if (keys.has(o.key))
            errors.push('Повторная операция');
        keys.add(o.key);
        if (o.ownInn !== model.ownInn || o.ownAccount !== model.account || !o.date.startsWith('2026-'))
            errors.push('Операция другого счёта или года');
        if (r.category === 'income' && o.direction !== 'receipt')
            errors.push('Списание не может быть поступлением');
        if (['exclude', 'expense'].includes(r.category) && r.taxKop !== 0)
            errors.push('Исключённая операция должна иметь нулевую налоговую сумму');
        if (r.reviewed && (r.category === 'exclude' || r.taxKop !== o.amountKop) && !r.reason.trim())
            errors.push('Укажите основание исключения или изменения суммы');
        if (r.category === 'refund' && r.reviewed) {
            const original = model.rows.find(x => x.operation.key === r.refundOf && x.category === 'income' && x.reviewed);
            if (o.direction !== 'refund' || !original || original.operation.date > o.date || original.operation.counterpartyInn !== o.counterpartyInn || r.taxKop > o.amountKop)
                errors.push('Возврат должен быть связан с ранее учтённым поступлением того же плательщика');
            refunds.set(r.refundOf, (refunds.get(r.refundOf) ?? 0) + r.taxKop);
        }
    }
    for (const [key, amount] of refunds) {
        const original = model.rows.find(r => r.operation.key === key);
        if (amount > (original?.taxKop ?? 0))
            errors.push('Возвраты превышают исходный налоговый доход');
    }
    const previous = model.previousIncomeKop === null ? 0 : taxExtra(model.previousIncomeKop, 2025);
    if (model.previousExtraUsedKop > previous)
        errors.push('Использованный 1% превышает взнос за 2025 год');
    const dates = ['03-31', '06-30', '09-30', '12-31'];
    model.allocations.forEach((a, i) => {
        const income = model.rows.filter(r => r.operation.date <= `2026-${dates[i]}` && r.reviewed).reduce((sum, r) => sum + (r.category === 'income' ? r.taxKop : r.category === 'refund' ? -r.taxKop : 0), 0);
        if (a.fixedKop > TAX_FIXED_2026)
            errors.push('Фиксированное уменьшение превышает 57 390 ₽');
        if (a.previousExtraKop > Math.max(0, previous - model.previousExtraUsedKop) || a.previousExtraKop > 0 && !model.previousExtraConfirmed)
            errors.push('Сначала подтвердите неиспользованный 1% за 2025 год');
        if (a.currentExtraKop > taxExtra(Math.max(0, income), 2026))
            errors.push('Вычет 1% за 2026 год превышает взнос по подтверждённому доходу');
        if (i && ['fixedKop', 'previousExtraKop', 'currentExtraKop'].some(k => a[k] < model.allocations[i - 1][k]))
            errors.push('Взносы указываются нарастающим итогом');
    });
    const paymentKeys = new Set();
    for (const p of model.payments) {
        const key = p.reference.trim().toLowerCase();
        if (paymentKeys.has(key))
            errors.push('Платёж с таким подтверждением уже записан');
        paymentKeys.add(key);
    }
    return [...new Set(errors)];
}
export function calculateMyTax(model, today) {
    const errors = taxValidation(model);
    let previousTax = 0;
    const periods = ['03-31', '06-30', '09-30', '12-31'].map((end, index) => {
        const to = `2026-${end}`, through = to < today ? to : today;
        const rows = model.rows.filter(r => r.operation.date <= through);
        const receiptsKop = rows.filter(r => r.reviewed && r.category === 'income').reduce((s, r) => s + r.taxKop, 0);
        const refundsKop = rows.filter(r => r.reviewed && r.category === 'refund').reduce((s, r) => s + r.taxKop, 0);
        const incomeKop = rows.reduce((sum, r) => sum + (r.reviewed && r.category === 'income' ? r.taxKop : r.reviewed && r.category === 'refund' ? -r.taxKop : 0), 0);
        let runningIncome = 0, peakIncome = 0;
        for (const row of [...rows].sort((a, b) => a.operation.date.localeCompare(b.operation.date))) {
            runningIncome += row.reviewed && row.category === 'income' ? row.taxKop : row.reviewed && row.category === 'refund' ? -row.taxKop : 0;
            peakIncome = Math.max(peakIncome, runningIncome);
        }
        const pending = rows.filter(r => !r.reviewed || r.category === 'review').length;
        const a = model.allocations[index], requestedKop = a.fixedKop + a.previousExtraKop + a.currentExtraKop;
        const declaredIncomeRub = Math.round(Math.max(0, incomeKop) / 100);
        const grossKop = Math.round(declaredIncomeRub * .06) * 100;
        const fixedUsedKop = Math.min(grossKop, Math.round(a.fixedKop / 100) * 100);
        // Round the total deduction once; rounded components must add up to that total.
        const fixedAndPreviousKop = Math.min(grossKop, Math.round((a.fixedKop + a.previousExtraKop) / 100) * 100);
        const previousUsedKop = fixedAndPreviousKop - fixedUsedKop;
        const currentUsedKop = Math.min(grossKop, Math.round(requestedKop / 100) * 100) - fixedAndPreviousKop;
        const netKop = grossKop - fixedUsedKop - previousUsedKop - currentUsedKop;
        const changeKop = netKop - previousTax;
        const priorKop = previousTax;
        previousTax = netKop;
        const gaps = missingBankPeriods('2026-01-01', through, model.coverage.filter(c => c.account === model.account), model.ownInn);
        const blockers = [...errors, ...(!model.profileConfirmed ? ['Подтвердите условия расчёта'] : []), ...(!model.sourcesConfirmed ? ['Подтвердите полноту доходов'] : []), ...(model.previousIncomeKop === null ? ['Укажите доход 2025 года для проверки НДС'] : []), ...(pending ? [`Операций на проверке: ${pending}`] : []), ...(gaps.length ? ['Выписка не покрывает весь период'] : []), ...(to > today ? ['Период ещё не завершён'] : []), ...(incomeKop < 0 ? ['Возвраты превышают доходы периода'] : []), ...(peakIncome > 2000000000 || (model.previousIncomeKop ?? 0) > 2000000000 ? ['Превышен порог освобождения от НДС. Нужен отдельный расчёт с учётом НДС'] : [])];
        return { index, to, receiptsKop, refundsKop, incomeKop, declaredIncomeRub, fixedUsedKop, previousUsedKop, currentUsedKop, grossKop, deductionKop: grossKop - netKop, requestedKop, netKop, priorKop, changeKop, pending, gaps, blockers: [...new Set(blockers)] };
    });
    return { periods, errors, fixedKop: TAX_FIXED_2026, previousExtraKop: model.previousIncomeKop === null ? null : taxExtra(model.previousIncomeKop, 2025), currentExtraKop: taxExtra(Math.max(0, periods[3].incomeKop), 2026) };
}
export function taxDeclarationRows(model, today) {
    const result = calculateMyTax(model, today);
    const out = [];
    const add = (section, line, label, value) => out.push({ section, line, label, value: String(value) });
    add('2.1.1', '102', 'Признак ИП, не производящего выплаты физическим лицам', 2);
    result.periods.forEach((p, i) => {
        const label = ['I квартал', 'Полугодие', '9 месяцев', 'Год'][i];
        add('2.1.1', String(110 + i), 'Доходы · ' + label, p.declaredIncomeRub);
        add('2.1.1', String(120 + i), 'Ставка · ' + label, 6);
        add('2.1.1', String(130 + i), 'Исчисленный налог · ' + label, p.grossKop / 100);
        add('2.1.1', String(140 + i), 'Уменьшение на взносы · ' + label, p.deductionKop / 100);
        const plus = ['020', '040', '070', '100'][i], minus = ['', '050', '080', '110'][i];
        add('1.1', plus, 'К уплате · ' + label, Math.max(0, p.changeKop) / 100);
        if (minus)
            add('1.1', minus, 'К уменьшению · ' + label, Math.max(0, -p.changeKop) / 100);
    });
    const annual = result.periods[3];
    add('2.1.1', '150', 'Фиксированные взносы, учтённые за год', annual.fixedUsedKop / 100);
    add('2.1.1', '160', 'Дополнительный 1%, учтённый за год', (annual.previousUsedKop + annual.currentUsedKop) / 100);
    add('2.1.1', '161', 'В том числе 1% за 2026 год', annual.currentUsedKop / 100);
    add('2.1.1', '162', 'В том числе 1% за 2025 год', annual.previousUsedKop / 100);
    return out;
}
/** Updates untouched suggestions, while preserving all explicit user decisions. */
export function prepareTaxRows(input) {
    const legacyReasons = ['Поступление по выписке', 'Нужна проверка налогового назначения', 'Проверьте, является ли это возвратом облагаемой оплаты'];
    const rows = input.map(r => !r.reviewed && r.decision !== 'manual' && legacyReasons.includes(r.reason) ? suggestTaxRow(r.operation) : { ...r });
    const used = new Map();
    for (const r of rows)
        if (r.category === 'refund' && r.reviewed)
            used.set(r.refundOf, (used.get(r.refundOf) ?? 0) + r.taxKop);
    for (const r of rows) {
        if (r.reviewed || r.decision === 'manual' || r.operation.direction !== 'refund' || !/возврат/i.test(r.operation.purpose))
            continue;
        const o = r.operation;
        const candidates = rows.filter(x => x.category === 'income' && x.reviewed && !!o.counterpartyInn && x.operation.counterpartyInn === o.counterpartyInn && x.operation.date <= o.date && x.taxKop - (used.get(x.operation.key) ?? 0) >= o.amountKop);
        const date = o.purpose.match(/оплачен[аоы]?\s+(\d{2})\.(\d{2})\.(\d{4})/i);
        const exact = candidates.filter(x => x.taxKop === o.amountKop && (!date || x.operation.date === date[3] + '-' + date[2] + '-' + date[1]));
        r.category = 'refund';
        r.taxKop = o.amountKop;
        r.decision = 'auto';
        if (exact.length === 1) {
            r.refundOf = exact[0].operation.key;
            r.reviewed = true;
            r.reason = 'Возврат связан с оплатой № ' + exact[0].operation.number + ' от ' + exact[0].operation.date;
            used.set(r.refundOf, (used.get(r.refundOf) ?? 0) + r.taxKop);
        }
        else {
            r.reviewed = false;
            r.refundOf = '';
            r.reason = candidates.length ? 'Найден возврат. Выберите, какую оплату вернули.' : 'Найден возврат, но исходная оплата отсутствует. Добавьте раннюю выписку или проверьте назначение.';
        }
    }
    return rows;
}
//# sourceMappingURL=myTax.js.map